import React, { useEffect, useState } from 'react';
import { useStore } from '../state/store';
import { Card, Banner, Empty } from '../components/ui';
import { api } from '../state/api';
import { planNeedsSetup } from '../lib/orgPlan';

/** Onboarding: select template, CSV import, or Jira epic import. */
export default function PlanSetup({ onDone }) {
  const { reload, state } = useStore();
  const [templates, setTemplates] = useState([]);
  const [csv, setCsv] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [jira, setJira] = useState({ baseUrl: '', email: '', apiToken: '', projectKey: '' });
  const [tab, setTab] = useState('template');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.listStrategyTemplates();
        if (!cancelled) setTemplates(res.items || []);
      } catch (err) {
        if (!cancelled) setError(err.message || String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function selectTemplate(templateId) {
    setBusy(true);
    setError('');
    try {
      await api.selectStrategyTemplate({ templateId, activate: true });
      await reload?.();
      onDone?.();
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy(false);
    }
  }

  async function previewCsv() {
    setBusy(true);
    setError('');
    try {
      const res = await api.previewStrategyCsv({ csv });
      setPreview(res);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy(false);
    }
  }

  async function importCsv() {
    setBusy(true);
    setError('');
    try {
      await api.importStrategyCsv({ csv, name: 'CSV import' });
      await api.approveStrategy({});
      await reload?.();
      onDone?.();
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy(false);
    }
  }

  async function saveJiraAndImport() {
    setBusy(true);
    setError('');
    try {
      await api.putJiraStrategyConnection(jira);
      await api.importStrategyJira({ projectKey: jira.projectKey });
      await api.approveStrategy({});
      await reload?.();
      onDone?.();
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy(false);
    }
  }

  if (!planNeedsSetup(state) && (state.plan?.epics || []).length) return null;

  return (
    <section className="section">
      <Card title="Set up your organization plan">
        <div className="sub" style={{ marginBottom: 12 }}>
          New organizations start without a strategy map. Choose a template, import a CSV outline, or pull Jira epics once.
        </div>
        {error && <Banner tone="red">{error}</Banner>}
        <div className="row wrap" style={{ gap: 8, marginBottom: 12 }}>
          {[
            { key: 'template', label: 'Template' },
            { key: 'csv', label: 'CSV import' },
            { key: 'jira', label: 'Jira epics' },
          ].map((t) => (
            <button
              key={t.key}
              type="button"
              className={`btn sm ${tab === t.key ? 'primary' : 'ghost'}`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'template' && (
          <div className="grid g2">
            {templates.length === 0 ? (
              <Empty>No templates available yet.</Empty>
            ) : (
              templates.map((t) => (
                <Card key={t.templateId} title={t.name} sub={t.description || `${t.epicCount} epics`}>
                  <button
                    type="button"
                    className="btn primary sm"
                    disabled={busy}
                    onClick={() => selectTemplate(t.templateId)}
                  >
                    Use this template
                  </button>
                </Card>
              ))
            )}
          </div>
        )}

        {tab === 'csv' && (
          <>
            <p className="dim tiny">
              Columns: kind (epic|gate|workPackage|stage), id, title, epic_id, gate_day, evidence, outcome…
            </p>
            <textarea
              className="input"
              rows={8}
              value={csv}
              onChange={(e) => setCsv(e.target.value)}
              placeholder={'kind,id,title,evidence\nepic,E01,Strategy Lock,Exception logged'}
              style={{ width: '100%', fontFamily: 'monospace' }}
            />
            <div className="row tight" style={{ marginTop: 8 }}>
              <button type="button" className="btn sm" disabled={busy || !csv.trim()} onClick={previewCsv}>
                Preview
              </button>
              <button
                type="button"
                className="btn sm primary"
                disabled={busy || !preview?.ok}
                onClick={importCsv}
              >
                Import &amp; activate
              </button>
            </div>
            {preview && (
              <div className="dim tiny" style={{ marginTop: 8 }}>
                {preview.ok
                  ? `${preview.suggestedEpics.length} epics, ${preview.suggestedGateOutcomes.length} gates, ${preview.suggestedWorkPackages.length} work packages`
                  : preview.errors.join('; ')}
                {preview.warnings?.length ? ` · warnings: ${preview.warnings.slice(0, 3).join('; ')}` : ''}
              </div>
            )}
          </>
        )}

        {tab === 'jira' && (
          <>
            <p className="dim tiny">One-time epic import only — stories stay in Jira.</p>
            <div className="grid g2">
              <label className="field">
                <span>Base URL</span>
                <input
                  className="input"
                  value={jira.baseUrl}
                  onChange={(e) => setJira({ ...jira, baseUrl: e.target.value })}
                  placeholder="https://your.atlassian.net"
                />
              </label>
              <label className="field">
                <span>Email</span>
                <input
                  className="input"
                  value={jira.email}
                  onChange={(e) => setJira({ ...jira, email: e.target.value })}
                />
              </label>
              <label className="field">
                <span>API token</span>
                <input
                  className="input"
                  type="password"
                  value={jira.apiToken}
                  onChange={(e) => setJira({ ...jira, apiToken: e.target.value })}
                  placeholder="Write-only"
                />
              </label>
              <label className="field">
                <span>Project key</span>
                <input
                  className="input"
                  value={jira.projectKey}
                  onChange={(e) => setJira({ ...jira, projectKey: e.target.value })}
                  placeholder="B45"
                />
              </label>
            </div>
            <button
              type="button"
              className="btn sm primary"
              style={{ marginTop: 8 }}
              disabled={busy || !jira.baseUrl || !jira.apiToken}
              onClick={saveJiraAndImport}
            >
              Import epics &amp; activate
            </button>
          </>
        )}
      </Card>
    </section>
  );
}
